import React, { useEffect, useState } from 'react';
import { Desktop } from './MobileCheckComponent';

const TIMELINES = {
  global: {
    label: 'Global',
    handle: 'GRDSMN_GLOBAL',
    href: 'https://x.com/GRDSMN_GLOBAL',
  },
  jp: {
    label: 'JP',
    handle: 'GRANDSUMMONERS',
    href: 'https://x.com/GRANDSUMMONERS',
  },
};

const TWEET_LIMIT = 5;

function timelineUrl(handle) {
  return `https://api.fxtwitter.com/2/profile/${encodeURIComponent(handle)}/statuses`;
}

function formatTweetDate(tweet) {
  const ms = tweet.created_timestamp
    ? (tweet.created_timestamp >= 1e12 ? tweet.created_timestamp : tweet.created_timestamp * 1000)
    : Date.parse(tweet.created_at);
  if (!Number.isFinite(ms)) {
    return '';
  }
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getMediaPreview(tweet) {
  const photo = tweet.media?.photos?.[0];
  if (photo?.url) {
    return { src: photo.url.replace('name=orig', 'name=small'), alt: 'Tweet image' };
  }
  const video = tweet.media?.videos?.[0];
  if (video?.thumbnail_url) {
    return { src: video.thumbnail_url, alt: 'Tweet video thumbnail' };
  }
  return null;
}

function TweetCard({ tweet }) {
  const media = getMediaPreview(tweet);
  const text = (tweet.text || '').trim();
  const date = formatTweetDate(tweet);

  return (
    <a
      className="gs-tweet"
      href={tweet.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="gs-tweet__meta">
        <span className="gs-tweet__author">@{tweet.author?.screen_name || 'unknown'}</span>
        {date ? <time className="gs-tweet__date">{date}</time> : null}
      </div>
      {text ? <p className="gs-tweet__text">{text}</p> : null}
      {media ? (
        <img className="gs-tweet__media" src={media.src} alt={media.alt} loading="lazy" />
      ) : null}
    </a>
  );
}

function TimelineFeed({ account }) {
  const [tweets, setTweets] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load() {
      setStatus('loading');
      setTweets([]);
      try {
        const res = await fetch(timelineUrl(account.handle), {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        const results = Array.isArray(data.results) ? data.results : [];
        const posts = results
          .filter((item) => item && (item.type === 'status' || item.id))
          .slice(0, TWEET_LIMIT);

        if (!cancelled) {
          setTweets(posts);
          setStatus(posts.length ? 'ready' : 'error');
        }
      } catch (err) {
        if (cancelled || err.name === 'AbortError') {
          return;
        }
        setStatus('error');
      }
    }

    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [account.handle]);

  if (status === 'loading') {
    return <p className="gs-twitter-panel__status">Loading posts…</p>;
  }

  if (status === 'error') {
    return (
      <p className="gs-twitter-panel__status">
        Could not load posts.{' '}
        <a href={account.href} target="_blank" rel="noopener noreferrer">
          Open @{account.handle} Twitter
        </a>
      </p>
    );
  }

  return (
    <div className="gs-tweet-list">
      {tweets.map((tweet) => (
        <TweetCard key={tweet.id} tweet={tweet} />
      ))}
    </div>
  );
}

const TwitterContainer = () => {
  const [server, setServer] = useState('global');
  const account = TIMELINES[server];

  return (
    <Desktop>
      <section className="gs-twitter-panel" aria-label="Official Twitter updates">
        <div className="gs-twitter-panel__head">
          <h2 className="gs-twitter-panel__title">Official News</h2>
          <div className="gs-field">
            <div className="gs-seg">
              {Object.entries(TIMELINES).map(([key, value]) => (
                <label key={key} className={server === key ? 'is-active' : ''}>
                  <input
                    type="radio"
                    name="twitter-server"
                    checked={server === key}
                    onChange={() => setServer(key)}
                    autoComplete="off"
                  />
                  {value.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <TimelineFeed key={account.handle} account={account} />

        <p className="gs-twitter-panel__fallback">
          <a href={account.href} target="_blank" rel="noopener noreferrer">
            View @{account.handle} on Twitter
          </a>
        </p>
      </section>
    </Desktop>
  );
};

export default TwitterContainer;
